import type { Section } from '@/api/types/core/sections';

/**
 * Calcule la distance de Levenshtein entre deux chaînes
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  const len1 = str1.length;
  const len2 = str2.length;

  // Initialiser la matrice
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Remplir la matrice
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,     // suppression
        matrix[i][j - 1] + 1,     // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Calcule le pourcentage de similarité entre deux chaînes (0-1)
 */
export function getSimilarity(str1: string, str2: string): number {
  if (str1 === str2) return 1;
  if (str1.length === 0 && str2.length === 0) return 1;
  if (str1.length === 0 || str2.length === 0) return 0;

  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  const maxLength = Math.max(str1.length, str2.length);
  return (maxLength - distance) / maxLength;
}

/**
 * Extrait la dernière partie d'un nom de section (après le dernier ".")
 */
function getLastPart(sectionName: string): string {
  const parts = sectionName.split('.');
  return parts[parts.length - 1];
}

/**
 * Interface pour une section similaire détectée
 */
export interface SimilarSection {
  section: Section;
  similarity: number;
  reason: 'exact_last_part' | 'similar_last_part' | 'similar_full_name';
}

/**
 * Détecte les sections similaires à un nom donné
 */
export function detectSimilarSectionNames(
  newSectionName: string, 
  existingSections: Section[], 
  minSimilarity: number = 0.7
): SimilarSection[] {
  const similarSections: SimilarSection[] = [];
  const newLastPart = getLastPart(newSectionName).toLowerCase();
  const newFullName = newSectionName.toLowerCase();

  for (const existingSection of existingSections) {
    const existingLastPart = getLastPart(existingSection.name).toLowerCase();
    const existingFullName = existingSection.name.toLowerCase();

    // Vérification 1: Dernière partie exactement identique
    if (newLastPart === existingLastPart && newSectionName !== existingSection.name) {
      similarSections.push({
        section: existingSection,
        similarity: 1.0,
        reason: 'exact_last_part'
      });
      continue;
    }

    // Vérification 2: Dernière partie similaire
    const lastPartSimilarity = getSimilarity(newLastPart, existingLastPart);
    if (lastPartSimilarity >= minSimilarity && lastPartSimilarity < 1.0) {
      similarSections.push({
        section: existingSection,
        similarity: lastPartSimilarity,
        reason: 'similar_last_part'
      });
      continue;
    }

    // Vérification 3: Nom complet similaire
    const fullNameSimilarity = getSimilarity(newFullName, existingFullName);
    if (fullNameSimilarity >= minSimilarity && fullNameSimilarity < 1.0) {
      similarSections.push({
        section: existingSection,
        similarity: fullNameSimilarity,
        reason: 'similar_full_name'
      });
    }
  }

  // Trier par similarité décroissante
  return similarSections.sort((a, b) => b.similarity - a.similarity);
}

/**
 * Génère un message descriptif pour expliquer la similarité
 */
export function getSimilarityMessage(similar: SimilarSection): string {
  const percentage = Math.round(similar.similarity * 100);
  
  switch (similar.reason) {
    case 'exact_last_part':
      return `Même dernière partie ("${getLastPart(similar.section.name)}") - 100% identique`;
    case 'similar_last_part':
      return `Dernière partie similaire ("${getLastPart(similar.section.name)}") - ${percentage}% similaire`;
    case 'similar_full_name':
      return `Nom complet similaire - ${percentage}% similaire`;
    default:
      return `${percentage}% similaire`;
  }
}

/**
 * Détecte si deux schémas sont similaires (nom + type)
 */
export interface SimilarSchema {
  schema: { uuid: string; appellation: string; type: string };
  similarity: number;
  reason: 'exact_name_and_type' | 'similar_name_and_type' | 'exact_name_different_type' | 'similar_name_same_type';
}

/**
 * Détecte les schémas similaires
 */
export function detectSimilarSchemas(
  newAppellation: string,
  newType: string,
  existingSchemas: { uuid: string; appellation: string; type: string }[],
  minSimilarity: number = 0.7
): SimilarSchema[] {
  const similarSchemas: SimilarSchema[] = [];

  for (const existingSchema of existingSchemas) {
    const nameSimilarity = getSimilarity(newAppellation, existingSchema.appellation);
    const sameType = newType.toLowerCase() === existingSchema.type.toLowerCase();

    // Vérification 1: Nom et type exactement identiques
    if (nameSimilarity === 1.0 && sameType) {
      similarSchemas.push({
        schema: existingSchema,
        similarity: 1.0,
        reason: 'exact_name_and_type'
      });
      continue;
    }

    // Vérification 2: Nom similaire et même type
    if (nameSimilarity >= minSimilarity && sameType) {
      similarSchemas.push({
        schema: existingSchema,
        similarity: nameSimilarity,
        reason: 'similar_name_and_type'
      });
      continue;
    }

    // Vérification 3: Nom exact mais type différent
    if (nameSimilarity === 1.0 && !sameType) {
      similarSchemas.push({
        schema: existingSchema,
        similarity: 0.9, // Légèrement réduit car type différent
        reason: 'exact_name_different_type'
      });
      continue;
    }

    // Vérification 4: Nom similaire et même type
    if (nameSimilarity >= minSimilarity) {
      const adjustedSimilarity = sameType ? nameSimilarity : nameSimilarity * 0.8;
      if (adjustedSimilarity >= minSimilarity) {
        similarSchemas.push({
          schema: existingSchema,
          similarity: adjustedSimilarity,
          reason: sameType ? 'similar_name_same_type' : 'similar_name_and_type'
        });
      }
    }
  }

  // Trier par similarité décroissante
  return similarSchemas.sort((a, b) => b.similarity - a.similarity);
}

/**
 * Génère un message descriptif pour expliquer la similarité de schéma
 */
export function getSchemaSimilarityMessage(similar: SimilarSchema): string {
  const percentage = Math.round(similar.similarity * 100);
  
  switch (similar.reason) {
    case 'exact_name_and_type':
      return `Nom et type identiques - ${percentage}% identique`;
    case 'similar_name_and_type':
      return `Nom similaire, même type - ${percentage}% similaire`;
    case 'exact_name_different_type':
      return `Nom identique, type différent ("${similar.schema.type}") - ${percentage}% similaire`;
    case 'similar_name_same_type':
      return `Nom similaire, même type - ${percentage}% similaire`;
    default:
      return `${percentage}% similaire`;
  }
} 